import React, { useEffect, useRef } from 'react';
import api from '../../config/axios';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

// Align with server throttle (10s) so we never hit "Location update too frequent"
const MIN_SEND_INTERVAL_MS = Math.max(10 * 1000, 30 * 1000); // at least 10s (server limit), 30s normal
const MIN_DISTANCE_METERS = 5; // send if moved > 5m

function haversineDistanceMeters(lat1, lon1, lat2, lon2) {
    const toRad = (v) => (v * Math.PI) / 180;
    const R = 6371000; // meters
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

const GeoTracker = () => {
    const { user } = useAuth();
    const watchIdRef = useRef(null);
    const lastSentRef = useRef({ time: 0, lat: null, lon: null });
    const toastShownRef = useRef(false);
    const permissionCheckedRef = useRef(false);

    useEffect(() => {
        if (!user) return;

        // Only track executives (sales_executive and team_manager)
        const allowedRoles = ['sales_executive', 'team_manager'];
        const userRole = user.roleId?.name || user.role;
        if (!allowedRoles.includes(userRole)) return;

        if (!('geolocation' in navigator)) {
            console.warn('Geolocation is not available in this browser');
            return;
        }

        // Check permission state and show toast only once per session
        const checkPermissionAndShowToast = async () => {
            // Check if we've already shown the toast in this session (using sessionStorage)
            const toastShown = sessionStorage.getItem('location-toast-shown');
            if (toastShown === 'true') {
                toastShownRef.current = true;
            }

            // Check if we've already checked permissions
            if (permissionCheckedRef.current) return;
            permissionCheckedRef.current = true;

            // Check current permission state
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

                    // If permission is already granted, don't show toast
                    if (permissionStatus.state === 'granted') {
                        toastShownRef.current = true;
                        sessionStorage.setItem('location-toast-shown', 'true');
                        return;
                    }

                    // If permission is denied, show a different message (only once)
                    if (permissionStatus.state === 'denied') {
                        if (!toastShownRef.current) {
                            toastShownRef.current = true;
                            sessionStorage.setItem('location-toast-shown', 'true');
                            toast.error('Location access is denied. Please enable it in your browser settings to enable tracking.', {
                                duration: 6000,
                                id: 'location-denied-toast'
                            });
                        }
                        return;
                    }
                } catch (e) {
                    // Permissions API not supported, continue with normal flow
                }
            }

            // Show consent info only once per session
            if (!toastShownRef.current) {
                toastShownRef.current = true;
                sessionStorage.setItem('location-toast-shown', 'true');
                toast((t) => (
                    <div className="text-sm">
                        <div className="font-medium mb-1">Allow access to your location</div>
                        <div className="mb-2">We track your real-time travel during working hours to improve visit planning and safety.</div>
                        <button onClick={() => toast.dismiss(t.id)} className="mt-1 text-blue-100 underline">OK</button>
                    </div>
                ), {
                    duration: 5000,
                    id: 'location-permission-toast' // Unique ID prevents duplicates
                });
            }
        };

        checkPermissionAndShowToast();

        const sendPosition = async (pos) => {
            const { latitude, longitude, accuracy, speed, heading, altitude } = pos.coords || {};
            const now = Date.now();

            // Never send within 10s of last send (server enforces this; avoid 429 and user-facing error)
            if (lastSentRef.current.time > 0 && now - lastSentRef.current.time < 10 * 1000) {
                return;
            }

            // Always enforce minimum interval throttle (30s) to prevent 429 rate limit
            if (now - lastSentRef.current.time < MIN_SEND_INTERVAL_MS) {
                return;
            }

            if (lastSentRef.current.lat != null) {
                const distance = haversineDistanceMeters(
                    lastSentRef.current.lat,
                    lastSentRef.current.lon,
                    latitude,
                    longitude
                );
                if (distance < MIN_DISTANCE_METERS) {
                    return;
                }
            }

            try {
                await api.post('/locations/log', {
                    executiveId: user?._id,
                    latitude,
                    longitude,
                    accuracy,
                    speed,
                    heading,
                    altitude,
                    timestamp: new Date().toISOString()
                });
                lastSentRef.current = { time: now, lat: latitude, lon: longitude };
            } catch (e) {
                console.error('Failed to send geo location', e);
            }
        };

        const onError = (err) => {
            // Only log non-critical errors (code 1 = permission denied, code 2 = position unavailable)
            // These are expected in development or when location services are disabled
            if (err.code === 1 || err.code === 2) {
                // Permission denied or position unavailable - these are expected, don't spam console
                return;
            }
            // Log other errors (code 3 = timeout) - but only once to avoid spam
            // Geolocation errors are benign for our use case (user denied,
            // timeout, kiosk with no GPS); swallow silently.
        };

        // Request permission and start tracking automatically
        // This will trigger the browser's permission prompt if not already granted
        try {
            // Send an immediate one-off position so Live view has data without waiting
            // This automatically requests permission if not already granted
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    sendPosition(pos);
                    // After getting first position, start continuous tracking
                    watchIdRef.current = navigator.geolocation.watchPosition(
                        sendPosition,
                        onError,
                        {
                            enableHighAccuracy: true,
                            maximumAge: 30 * 1000,
                            timeout: 30 * 1000 // 30 second timeout for continuous tracking
                        }
                    );
                },
                onError,
                {
                    enableHighAccuracy: true,
                    maximumAge: 0,
                    timeout: 15000 // Increased timeout to 15 seconds to reduce timeout errors
                }
            );
        } catch (e) {
            // Only log in development
            if (process.env.NODE_ENV === 'development') {
                console.error('Failed to start geolocation watch', e);
            }
        }

        const handleBeforeUnload = () => {
            if (watchIdRef.current != null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (watchIdRef.current != null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
        };
    }, [user]);

    return null;
};

export default GeoTracker;


